import { FieldValues } from 'react-hook-form';
import { AssessmentsQuestionViewModel, AssessmentsQuestionViewModels } from '../types';
import { ASSESSMENTS_MIN_DATE, ASSESSMENTS_VALIDATION_ERRORS } from '../constants';

interface AnswerValidation {
  isRequired: boolean;
  isUnanswered: boolean;
  isInvalidDate?: boolean;
}

export const isValidAssessmentDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return false;
  }

  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);

  const assessmentsMinYear = Number(ASSESSMENTS_MIN_DATE.split('-')[0]);

  if (year < assessmentsMinYear || year > 9999 || month < 1 || month > 12) {
    return false;
  }

  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  return day >= 1 && day <= daysInMonth;
};

const validateAnswer = (
  question: AssessmentsQuestionViewModel,
  values: FieldValues,
): AnswerValidation => {
  const validation: AnswerValidation = {
    isRequired: false,
    isUnanswered: false,
  };

  switch (question.type) {
    case 'radio':
    case 'checkbox': {
      let value: string[] | undefined = values[question.id];

      if (
        question.type === 'radio' &&
        values[question.id] !== undefined &&
        values[question.id] !== null
      ) {
        value = [values[question.id]]; // Convert to array for uniform processing
      }

      if (value === undefined || value === null || value.length === 0) {
        validation.isUnanswered = true;
      }

      if (question.isRequired && validation.isUnanswered) {
        validation.isRequired = true;

        return validation;
      }

      let subQuestions: AssessmentsQuestionViewModels = [];

      for (let i = 0; i < question.options.length; i++) {
        const option = question.options[i];

        if (option.isOptionRequired && (!value || !value.includes(option.value))) {
          validation.isRequired = true;

          return validation;
        }

        if (value && value.includes(option.value) && option.subQuestions) {
          subQuestions = [...subQuestions, ...option.subQuestions];
        }
      }

      for (let i = 0; i < subQuestions.length; i++) {
        const subQuestion = subQuestions[i];

        const subQuestionValidation = validateAnswer(subQuestion, values);

        if (subQuestionValidation.isUnanswered) {
          validation.isUnanswered = true;
        }

        if (subQuestionValidation.isRequired) {
          validation.isRequired = true;
          return validation;
        }
      }

      return validation;
    }
    case 'textbox':
    case 'textarea': {
      const value = values[question.id] as string;
      if (value === undefined || value === null || value === '') {
        validation.isUnanswered = true;
      }
      if (question.isRequired && validation.isUnanswered) {
        validation.isRequired = true;
      }
      return validation;
    }
    case 'select': {
      const value = values[question.id];
      if (value === undefined || value === null || value === '') {
        validation.isUnanswered = true;
      }
      if (question.isRequired && validation.isUnanswered) {
        validation.isRequired = true;
      }
      return validation;
    }
    case 'date': {
      const value = values[question.id];
      if (value === undefined || value === null || value === '') {
        validation.isUnanswered = true;
      }
      if (question.isRequired && validation.isUnanswered) {
        validation.isRequired = true;
      }
      if (!validation.isUnanswered && typeof value === 'string' && !isValidAssessmentDate(value)) {
        validation.isInvalidDate = true;
      }
      return validation;
    }
    case 'unsupported': {
      if (question.isRequired) {
        validation.isRequired = true;
        return validation;
      }

      validation.isUnanswered = true;
      return validation;
    }
    default:
      return validation;
  }
};

interface AllAnswersValidation {
  isRequired: boolean;
  isUnanswered: boolean;
  isInvalidDate?: boolean;
  questionId: number;
}

export const validateAssessmentsAnswers = (
  questions: AssessmentsQuestionViewModels,
  values: FieldValues,
  skippedQuestions: number[],
): AllAnswersValidation | null => {
  let firstUnAnsweredQuestion: number | null = null;

  for (let i = 0; i < questions.length; i++) {
    if (!skippedQuestions.includes(i + 1)) {
      const { isRequired, isUnanswered, isInvalidDate } = validateAnswer(questions[i], values);
      if (!firstUnAnsweredQuestion && isUnanswered) {
        firstUnAnsweredQuestion = questions[i].id;
      }

      if (isRequired || isInvalidDate) {
        return {
          isRequired,
          isUnanswered,
          isInvalidDate,
          questionId: questions[i].id,
        };
      }
    }
  }

  if (firstUnAnsweredQuestion !== null) {
    return {
      isRequired: false,
      isUnanswered: true,
      questionId: firstUnAnsweredQuestion,
    };
  }

  return null;
};

// Remove this when AssessmentsQuestionField is refactored to use React Hook Form native validation
export const validateSingleField = (
  question: { type?: string; isRequired?: boolean; questionText?: string },
  value: any,
): { isValid: boolean; errorMessage?: string } => {
  if (question.type === 'date') {
    if (!value) {
      return { isValid: true };
    }

    return isValidAssessmentDate(value)
      ? { isValid: true }
      : { isValid: false, errorMessage: ASSESSMENTS_VALIDATION_ERRORS.DATE_INVALID };
  }

  if (question.isRequired && (!value || value === '')) {
    return { isValid: false, errorMessage: ASSESSMENTS_VALIDATION_ERRORS.FIELD_REQUIRED };
  }
  return { isValid: true };
};
